import { cx } from "@its-engineering-and-development/ui-foundation/utils";

export interface TabToggleOption<T extends string = string> {
    key: T;
    label: string;
    isDisabled?: boolean;
}

export interface TabToggleProps<T extends string = string> {
    activeTab: T;
    options: readonly [TabToggleOption<T>, TabToggleOption<T>];
    onTabChange: (tab: T) => void;
}

export function TabToggle<T extends string>({ activeTab, options, onTabChange }: TabToggleProps<T>) {
    const [leftOption, rightOption] = options;
    const isLeftActive = activeTab === leftOption.key;
    const isRightActive = activeTab === rightOption.key;

    return (
        <div className="relative inline-flex rounded-lg bg-gray-200 p-1" role="group">
            <div
                className={cx(
                    "absolute top-1 bottom-1 rounded-md bg-blue-600 shadow-md transition-all duration-300 ease-out",
                    isLeftActive ? "right-[50%] left-1" : "right-1 left-[50%]",
                )}
            />

            <button
                type="button"
                className={cx(
                    "relative z-10 rounded-md px-8 py-2.5 text-sm font-semibold transition-colors duration-200",
                    isLeftActive ? "text-white" : "text-gray-600 hover:text-gray-900",
                    leftOption.isDisabled && "cursor-not-allowed text-gray-400 hover:text-gray-400",
                )}
                onClick={() => onTabChange(leftOption.key)}
                aria-pressed={isLeftActive}
                disabled={leftOption.isDisabled}
            >
                {leftOption.label}
            </button>

            <button
                type="button"
                className={cx(
                    "relative z-10 rounded-md px-8 py-2.5 text-sm font-semibold transition-colors duration-200",
                    isRightActive ? "text-white" : "text-gray-600 hover:text-gray-900",
                    rightOption.isDisabled && "cursor-not-allowed text-gray-400 hover:text-gray-400",
                )}
                onClick={() => onTabChange(rightOption.key)}
                aria-pressed={isRightActive}
                disabled={rightOption.isDisabled}
            >
                {rightOption.label}
            </button>
        </div>
    );
}
